import type { MessageCounts } from '@/lib/types';
import { fmtNumber } from '@/lib/format';
import { cn } from '@/lib/utils';

const EMPTY: MessageCounts = { queued: 0, sending: 0, sent: 0, delivered: 0, failed: 0, optedOut: 0, invalid: 0 };

/** The five outcomes a recipient can land in, in the order the bar draws them. */
export const DELIVERY_SEGMENTS = [
  { key: 'delivered', label: 'Delivered', className: 'bg-foreground', value: (c: MessageCounts) => c.delivered },
  { key: 'sent', label: 'Sent, awaiting receipt', className: 'bg-foreground/55', value: (c: MessageCounts) => c.sent },
  { key: 'pending', label: 'Pending', className: 'bg-foreground/25', value: (c: MessageCounts) => c.queued + c.sending },
  { key: 'failed', label: 'Failed', className: 'bg-destructive', value: (c: MessageCounts) => c.failed },
  { key: 'skipped', label: 'Skipped', className: 'bg-muted-foreground/30', value: (c: MessageCounts) => c.optedOut + c.invalid },
] as const;

/** "14 delivered · 3 failed · 2 pending": the non-zero outcomes, delivered always first. */
export function deliverySummary(counts: MessageCounts | undefined, opts: { short?: boolean } = {}): string {
  const c = counts ?? EMPTY;
  const parts: string[] = [`${fmtNumber(c.delivered)} delivered`];
  if (c.failed > 0) parts.push(`${fmtNumber(c.failed)} failed`);
  if (c.sent > 0) parts.push(`${fmtNumber(c.sent)} sent`);
  if (c.queued + c.sending > 0) parts.push(`${fmtNumber(c.queued + c.sending)} pending`);
  if (!opts.short) {
    if (c.optedOut > 0) parts.push(`${fmtNumber(c.optedOut)} opted out`);
    if (c.invalid > 0) parts.push(`${fmtNumber(c.invalid)} invalid`);
  } else if (c.optedOut + c.invalid > 0) parts.push(`${fmtNumber(c.optedOut + c.invalid)} skipped`);
  return parts.join(' · ');
}

/**
 * One message's outcomes as a stacked bar: delivered in ink, awaiting and pending lighter, failed in red, skipped
 * grey. Shares its scale with the recipient count so a half-full bar means half the people were reached. Used
 * on History rows, the Overview's recent list and the message page, so the shape reads the same everywhere.
 */
export function DeliveryBar({ counts, total, size = 'sm', className, label }: { counts: MessageCounts | undefined; total: number; size?: 'sm' | 'md'; className?: string; label?: string }) {
  const c = counts ?? EMPTY;
  const sum = DELIVERY_SEGMENTS.reduce((n, s) => n + s.value(c), 0);
  const scale = Math.max(total, sum, 1);
  return (
    <div className={cn('bg-foreground/10 flex w-full overflow-hidden', size === 'sm' ? 'h-1.5' : 'h-2.5', className)} role="img" aria-label={label ?? deliverySummary(c)} title={label ?? deliverySummary(c)}>
      {DELIVERY_SEGMENTS.map((s) => {
        const v = s.value(c);
        if (v <= 0) return null;
        return <span key={s.key} className={cn('h-full shrink-0 transition-[width] duration-300', s.className)} style={{ width: `${(v / scale) * 100}%` }} />;
      })}
    </div>
  );
}

/** Legend swatch for one segment, for tables that spell the bar out. */
export function DeliverySwatch({ segment, className }: { segment: (typeof DELIVERY_SEGMENTS)[number]['key']; className?: string }) {
  const s = DELIVERY_SEGMENTS.find((x) => x.key === segment);
  return <span className={cn('inline-block size-2.5 shrink-0', s?.className, className)} aria-hidden />;
}
